import { ReactNode, useCallback, useEffect, useRef, useState } from "react";
import { ConfirmDialog } from "./Modal";

export type UnsavedGuardLabels = {
  /** "Leave without saving?" */
  title: string;
  /** "You have N unsaved changes to <name>. If you leave, they're lost." (the page puts the name in <bdi>). */
  body: (count: number) => ReactNode;
  /** "Keep editing" — the safe button, focused. */
  keep: string;
  /** "Discard changes" */
  discard: string;
};

/* ---------- the frame's leave registry ---------- */

type Ask = (go: () => void) => void;
/** The guards of the pages that hold unsaved changes now (the newest asks). Module state: one app, one frame. */
const guards: Ask[] = [];

/**
 * The new frame's own navigation (side menu, account menu) goes through this: with unsaved changes on the page it
 * asks first (the page's own "Leave without saving?"), else it goes at once. The browser's Back is not covered.
 */
export const guardedLeave = (go: () => void): void => {
  const ask = guards[guards.length - 1];
  if (ask) ask(go);
  else go();
};
/** A page holds unsaved changes now (a link should then wait for `guardedLeave` instead of following its href). */
export const hasUnsavedChanges = (): boolean => guards.length > 0;

/**
 * Board 44 B. `confirmLeave(go)`: nothing changed → `go()` at once; otherwise asks first and runs `go` only on
 * "Discard changes". While there are changes the browser's own leave (reload, closing the tab) asks too, and so do the
 * new frame's menus (`guardedLeave`); the browser's Back button does not (today's router cannot hold it).
 * Render `dialog` once in the page.
 */
export const useUnsavedGuard = (
  dirtyCount: number,
  labels: UnsavedGuardLabels,
  /** `onDiscard`: runs when "Discard changes" is chosen, before leaving (e.g. a page drops its draft kept on the device). */
  opts: { onDiscard?: () => void } = {},
): { confirmLeave: (go: () => void) => void; dialog: ReactNode } => {
  const onDiscard = useRef(opts.onDiscard);
  onDiscard.current = opts.onDiscard;
  const [asking, setAsking] = useState(false);
  const pending = useRef<(() => void) | null>(null);
  const dirty = dirtyCount > 0;

  /** "Discard changes" was chosen: the browser's own leave must not ask a second time (sign out, switch account reload). */
  const leaving = useRef(false);
  useEffect(() => {
    leaving.current = false;
    if (!dirty) return undefined;
    const hold = (e: BeforeUnloadEvent) => { if (leaving.current) return; e.preventDefault(); e.returnValue = ""; };
    window.addEventListener("beforeunload", hold);
    return () => window.removeEventListener("beforeunload", hold);
  }, [dirty]);

  const confirmLeave = useCallback((go: () => void) => {
    if (!dirty) { go(); return; }
    pending.current = go;
    setAsking(true);
  }, [dirty]);

  // While dirty the frame's menus ask through this page's dialog too.
  useEffect(() => {
    if (!dirty) return undefined;
    const ask: Ask = (go) => confirmLeave(go);
    guards.push(ask);
    return () => { const i = guards.indexOf(ask); if (i >= 0) guards.splice(i, 1); };
  }, [dirty, confirmLeave]);

  const keep = () => { pending.current = null; setAsking(false); };
  const discard = () => {
    const go = pending.current;
    pending.current = null;
    setAsking(false);
    leaving.current = true;
    onDiscard.current?.();
    go?.();
  };

  const dialog = (
    <ConfirmDialog open={asking} title={labels.title} body={labels.body(dirtyCount)} cancelLabel={labels.keep} confirmLabel={labels.discard}
      onCancel={keep} onConfirm={discard} />
  );
  return { confirmLeave, dialog };
};
